// Sticky pasek na dole strony oferty: ilość pozycji, limit kredytowy klienta,
// suma netto/brutto, akcje "Zapisz wersję roboczą" + "Wyślij do klienta".

import React, { useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import {
  getSalesCrmOfferVersionsQueryKey,
  useGetSalesCrmClientOffer,
  useGetSalesCrmOfferTotals,
  usePostSalesCrmOfferVersion,
  usePutSalesCrmOffer
} from 'api';
import { useSendOfferToClientFlow } from 'hooks';

import SaveDraftVersionModal from './SaveDraftVersionModal';
import SendOfferToClientModal from 'components/containers/SendOfferToClientModal';

import styles from 'theme/components/containers/TraderClientOfferStickyBar/TraderClientOfferStickyBar.module.scss';

interface IProps {
  clientId: number;
  offerId: string;
  /** Wymuszona wersja oferty (null => aktualna). */
  versionId?: number | null;
  /** Wywołane po utworzeniu nowej wersji - pozwala stronie wybrać tę nową wersję w selekcie. */
  onVersionCreated?: (newVersionId: number) => void;
}

const formatCurrency = (raw: number | null | undefined): string => {
  if (raw == null || Number.isNaN(raw)) return '-';
  return `${raw.toLocaleString('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} zł`;
};

const TraderClientOfferStickyBar: React.FC<IProps> = ({
  clientId,
  offerId,
  versionId = null,
  onVersionCreated
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: totals } = useGetSalesCrmOfferTotals(clientId, offerId, versionId);
  const { data: offer } = useGetSalesCrmClientOffer(clientId, offerId, versionId);
  const [isDraftModalOpen, setIsDraftModalOpen] = useState(false);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);

  const statusId = offer?.status_id ?? null;
  const isInNegotiation = statusId === 1;
  const isToAccept = statusId === 6;

  const { mutate: putOffer, isLoading: isSavingStatus } = usePutSalesCrmOffer(
    clientId,
    offerId,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['sales-crm', 'client', clientId, 'offer', offerId]);
      }
    }
  );

  const sendFlow = useSendOfferToClientFlow({
    clientId,
    offerId,
    acceptanceUrl: offer?.acceptance_url ?? null
  });

  const handleSendOnly = async () => {
    await sendFlow.sendOnly();
    setIsSendModalOpen(false);
  };

  const handleSendAndOpen = async () => {
    await sendFlow.sendAndOpen();
    setIsSendModalOpen(false);
  };

  const positionsCount = totals?.positionsCount ?? 0;
  const totalNet = totals?.totalNet ?? 0;
  const totalGross = totals?.totalGross ?? 0;
  // Pozostały limit = dostępny limit kredytowy klienta (trade_credit_free) - wartość oferty brutto.
  // Może być ujemny (czerwone wskazanie) gdy oferta przekracza limit.
  const creditFree = totals?.clientCreditLimitRemaining ?? null;
  const creditRemaining = creditFree !== null ? creditFree - totalGross : null;

  const { mutate: createNewVersion, isLoading: isCreatingVersion } = usePostSalesCrmOfferVersion(
    clientId,
    offerId,
    {
      onSuccess: (response) => {
        const data = response?.data ?? {};
        const newId =
          (typeof data.newVersionId === 'number' ? data.newVersionId : null) ??
          (typeof data.new_version_id === 'number' ? data.new_version_id : null);
        queryClient.invalidateQueries(getSalesCrmOfferVersionsQueryKey(clientId, offerId));
        setIsDraftModalOpen(false);
        if (newId !== null && onVersionCreated) {
          onVersionCreated(newId);
        }
      }
    }
  );

  const handleCreateNewVersion = () => {
    if (isCreatingVersion) return;
    createNewVersion();
  };

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Containers-TraderClientOfferStickyBar'
      )}>
      <div className={styles.leftBlock}>
        <div className={styles.row}>
          <span className={styles.label}>{t('Ilość produktów')}:</span>
          <span className={styles.value}>
            {positionsCount} {t('szt.')}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>{t('Pozostały limit')}:</span>
          <span className={styles.value}>
            {creditRemaining !== null ? `${formatCurrency(creditRemaining)} (${t('brutto')})` : '-'}
          </span>
        </div>
      </div>

      <div className={styles.middleBlock}>
        <div className={styles.sumPrimary}>
          {t('Suma')}: {formatCurrency(totalNet)} ({t('netto')})
        </div>
        <div className={styles.sumSecondary}>
          {formatCurrency(totalGross)} ({t('brutto')})
        </div>
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.actionBtn}
          onClick={() => setIsDraftModalOpen(true)}>
          <span className={styles.btnIcon}>+</span>
          {t('ZAPISZ WERSJĘ ROBOCZĄ')}
        </button>
        {isToAccept ? (
          <button
            type="button"
            className={styles.actionBtn}
            onClick={() => putOffer({ status: 1 })}
            disabled={isSavingStatus}>
            <span className={styles.btnIcon}>+</span>
            {t('COFNIJ DO EDYCJI')}
          </button>
        ) : (
          <button
            type="button"
            className={styles.actionBtn}
            onClick={() => setIsSendModalOpen(true)}
            disabled={!isInNegotiation || isSavingStatus}>
            <span className={styles.btnIcon}>+</span>
            {t('WYŚLIJ DO KLIENTA')}
          </button>
        )}
        {!isInNegotiation && sendFlow.hasClientUser && (
          <button
            type="button"
            className={styles.actionBtn}
            onClick={() => sendFlow.openInClientPanel()}
            disabled={sendFlow.isSending}>
            <span className={styles.btnIcon}>+</span>
            {sendFlow.isExternalAcceptance
              ? t('PRZEJDŹ DO OFERTY')
              : t('ZOBACZ W PANELU KLIENTA')}
          </button>
        )}
      </div>

      <SaveDraftVersionModal
        open={isDraftModalOpen}
        isCreating={isCreatingVersion}
        onClose={() => setIsDraftModalOpen(false)}
        onCreateNewVersion={handleCreateNewVersion}
      />

      <SendOfferToClientModal
        open={isSendModalOpen}
        isSending={sendFlow.isSending}
        onClose={() => setIsSendModalOpen(false)}
        onSendOnly={handleSendOnly}
        onSendAndOpen={handleSendAndOpen}
        isExternalAcceptance={sendFlow.isExternalAcceptance}
      />
    </div>
  );
};

export default TraderClientOfferStickyBar;
